import React from 'react';
import { TopBar } from '../../components/layout/TopBar';
import { Header } from '../../components/layout/Header';
import { Navigation } from '../../components/layout/Navigation';
import { MobileStickyBar } from '../../components/layout/MobileStickyBar';
import { Footer } from '../../components/layout/Footer';
import { useScreenInit } from '../../useScreenInit';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Wrench, AlertTriangle, Info, CheckCircle2, MapPin, Signpost, Mail, Building, ArrowRight } from 'lucide-react';
export function InstallInstructions() {
  usePageTitle('Install Instructions | MidAtlantic Mailboxes');
  useScreenInit();
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-1306" data-mp="64ni0530d1ya">
      <TopBar data-id="element-1307" data-mp="67066zzd08js" />
      <Header data-id="element-1308" data-mp="2lw1jhxe7jv40" />
      <Navigation data-id="element-1309" data-mp="2lwbejze40sgk" />

      <main className="flex-grow" data-id="element-1310" data-mp="2lv08tu13tamt">
        {/* Hero Section */}
        <section className="bg-white border-b border-brand-border py-16 lg:py-24" data-id="element-1311" data-mp="2qv4r9plkoffo">
          <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-1312" data-mp="2qsrzyltxcspy">
            <span className="inline-block py-1 px-3 rounded-full bg-brand-goldSoft text-brand-charcoal text-xs font-bold tracking-wide mb-4 uppercase border border-brand-gold/30" data-id="element-1313" data-mp="fnsh3w0jd4un">
              Installation Guides
            </span>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-brand-charcoal mb-6 leading-tight" data-id="element-1314" data-mp="olb5999h7zar">
              Mailbox & Sign Installation
            </h1>
            <p className="text-lg lg:text-xl text-brand-muted max-w-3xl mx-auto leading-relaxed" data-id="element-1315" data-mp="yk0pv9srbxup">
              Detailed direct burial instructions for street signs and
              residential mailboxes, plus CBU installation diagrams. Please read
              all instructions and safety warnings carefully before beginning
              your project.
            </p>
          </div>
        </section>

        {/* Concrete Warning Callout (Global) */}
        <section className="py-8 bg-brand-background" data-id="element-1316" data-mp="1cdrx2bj0n5n9">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1317" data-mp="1cb5ivt1mdrw2">
            <div className="bg-brand-maroon/5 border border-brand-maroon/20 rounded-xl p-6 flex flex-col md:flex-row gap-6 items-start" data-id="element-1318" data-mp="a0n4ywdcce94">
              <div className="w-12 h-12 rounded-full bg-brand-maroon/10 text-brand-maroon flex items-center justify-center shrink-0" data-id="element-1319" data-mp="e1p6xkyn74es">
                <AlertTriangle size={24} data-id="element-1320" data-mp="nqyxgwdu8f0s" />
              </div>
              <div data-id="element-1321" data-mp="nsjs0m05jxyn">
                <h3 className="text-lg font-bold text-brand-maroon mb-2" data-id="element-1322" data-mp="nt377uyf488z">
                  CRITICAL: Use DRY Concrete Powder
                </h3>
                <p className="text-brand-charcoal/80 mb-3 text-sm leading-relaxed" data-id="element-1323" data-mp="xodeax3ob5tm">
                  <strong data-id="element-1324" data-mp="11qru18c1ppgo">
                    Do not follow instructions on the concrete bag.
                  </strong>{' '}
                  Completely wet concrete will cause the post to slowly lean as
                  it dries due to the weight of the system. The dry concrete
                  powder will absorb moisture from the soil to cure over 48-72
                  hours.
                </p>
                <p className="text-brand-charcoal/80 text-sm leading-relaxed mb-3" data-id="element-1325" data-mp="30z06twe8oxey">
                  If you have dry soil/climate, add a small amount of water to
                  the dry concrete powder but not enough to saturate. Do not
                  mix. Then, add water to the soil layer to increase moisture
                  for absorption. Keep the soil damp, not saturated.
                </p>
                <p className="text-brand-charcoal/80 text-sm leading-relaxed font-medium" data-id="element-1326" data-mp="3jvrj3lvd6c9i">
                  Heavily-concreted installations can be dangerous for drivers
                  and make future removal very difficult. If a concrete ball is
                  too large to remove later, you cannot re-use the hole and must
                  cut the post at ground level.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Street Sign Instructions */}
        <section className="py-16 bg-white border-b border-brand-border" id="street-signs" data-id="element-1327" data-mp="1uh373lqoqwc0">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1328" data-mp="3ichkn8oijrz4">
            <div className="flex items-center gap-4 mb-8" data-id="element-1329" data-mp="3n5kgjngrjdih">
              <div className="w-14 h-14 rounded-full bg-brand-goldWash text-brand-charcoal flex items-center justify-center shadow-sm" data-id="element-1330" data-mp="3n7413q9gcaq2">
                <Signpost size={28} data-id="element-1331" data-mp="3depw8on2unzo" />
              </div>
              <h2 className="text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1332" data-mp="3dd4yyr9doqy4">
                Street Sign Direct Burial
              </h2>
            </div>

            <p className="text-brand-muted mb-8 italic text-sm" data-id="element-1333" data-mp="9xg2gcf5ysc1">
              *These instructions apply to systems provided by MidAtlantic
              Mailbox, however they can be used to reference other
              manufacturers. Premiere Decorative Street Sign Systems include set
              screws. Hardware from other providers may differ.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-1334" data-mp="2krbtub0m1086">
              {/* Helpful Tips */}
              <div className="bg-brand-background p-6 rounded-xl border border-brand-border" data-id="element-1335" data-mp="2d6hznjvlcwyg">
                <h3 className="font-bold text-brand-charcoal mb-4 flex items-center gap-2" data-id="element-1336" data-mp="288zpjq5u8bov">
                  <Info size={18} className="text-brand-maroon" data-id="element-1337" data-mp="2n60hnurxy8nv" />
                  Helpful Tips & Guidelines
                </h3>
                <ul className="space-y-3 text-sm text-brand-muted" data-id="element-1338" data-mp="2i7p7fixqhw54">
                  <li className="flex items-start gap-2" data-id="element-1339" data-mp="1uga3xmu818sa">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1340" data-mp="1uep8zw3zvntw"></span>
                    <span data-id="element-1341" data-mp="1pfm4ccrxfjmg">
                      We typically install <strong data-id="element-1342" data-mp="246v1d6y5s79m">24"-30"</strong> in the
                      ground (if grade allows) for stability, rather than the
                      standard 18".
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1343" data-mp="1bp0qx3l8s1vy">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1344" data-mp="16rh1d7j3g2op"></span>
                    <span data-id="element-1345" data-mp="1wfmfq1phxd0w">
                      Hole diameter should be approx.{' '}
                      <strong data-id="element-1346" data-mp="20gy9mjfekkew">6"-8" wide</strong>.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1347" data-mp="257oelvjx2l92">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1348" data-mp="2587xpflazfoo"></span>
                    <span data-id="element-1349" data-mp="2a69ji8a45t3u">
                      <strong data-id="element-1350" data-mp="2a8lw05x3rb9i">Federal guidelines require</strong> a minimum of
                      84" from the bottom of the sign to the road surface. Local
                      jurisdictions may require additional heights.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1351" data-mp="1mfvm028rf21v">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1352" data-mp="1rgjnjhum71ac"></span>
                    <span data-id="element-1353" data-mp="2xx534i4f1i00">
                      Federal Highway requires 7' back from the road surface for
                      pedestrian safety.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 font-semibold text-brand-maroon" data-id="element-1354" data-mp="r8cxtr1gjfv6">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" data-id="element-1355" data-mp="m8gttrsrfofm" />
                    <span data-id="element-1356" data-mp="hia6rwdwcbzh">
                      Always call your local Ms. Utility to mark underground
                      lines prior to digging.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Required Materials */}
              <div className="bg-brand-background p-6 rounded-xl border border-brand-border" data-id="element-1357" data-mp="8fipvh294o0g">
                <h3 className="font-bold text-brand-charcoal mb-4 flex items-center gap-2" data-id="element-1358" data-mp="3h7jxhf2uxni">
                  <Wrench size={18} className="text-brand-maroon" data-id="element-1359" data-mp="3up26bikloznh" />
                  Required Materials
                </h3>
                <ul className="space-y-2 text-sm text-brand-muted" data-id="element-1360" data-mp="3ps392r5n4cl2">
                  <li className="flex items-center gap-2" data-id="element-1361" data-mp="1swx7ro8lj43s">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1362" data-mp="1ny42gj3gqjep" /> 12'
                    - 14' Pole
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1363" data-mp="wtjlx8hbshzh">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1364" data-mp="11smdbq4dupoz" />{' '}
                    Finial Topper with (2) Set Screws
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1365" data-mp="nylp3qb24l2v">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1366" data-mp="s189j65ef3q1" /> Base
                    with (3) or (4) Set Screws
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1367" data-mp="e4v44ve52srx">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1368" data-mp="j2ehlt30j1it" /> Sign
                    Brackets & Sign Blades
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1369" data-mp="546l1m9w2286">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1370" data-mp="93xmql0ye2rh" />{' '}
                    Minimum 5/8" Bolt for Bracket Installation
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1371" data-mp="23hyjn0cy0fox">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1372" data-mp="23fcawxebfu54" />{' '}
                    Digging Iron & Tamper Bar
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1373" data-mp="3jyoycpzvs54l">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1374" data-mp="3jw2pmn197jks" />{' '}
                    Allen Key/Allen Wrench
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1375" data-mp="3sxj0evvzdnpj">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1376" data-mp="bi2fdmdmiqb7" /> 60lb
                    Bag of DRY Concrete Powder (SAKRETE recommended)
                  </li>
                </ul>
              </div>
            </div>

            {/* Steps */}
            <h3 className="font-bold text-brand-charcoal text-xl mb-6" data-id="element-1377" data-mp="khh3if6biosj">
              Assembly & Installation Steps
            </h3>
            <p className="text-brand-maroon font-medium mb-6 text-sm" data-id="element-1378" data-mp="fhuteelsdony">
              *Assemble the street sign system PRIOR to installing in the
              ground.
            </p>

            <div className="space-y-4" data-id="element-1379" data-mp="2hcvi3b3ed4lj">
              {['Measure 24"-30" starting from the bottom of the pole. Mark this measurement. This will be the section installed into the ground.', 'On the top of the pole, install the finial topper using the (2) set screws provided and an Allen Wrench. Mark the holes for the sign bracket allowing your desired distance between the finial and bracket.', 'Before drilling, measure to ensure a minimum of 84" between the bottom of the sign bracket and the mark made in Step 1 (Federal Guidelines). If installing a dual sign, rotate the pole and mark holes for the second sign. Drill the holes.', 'Attach your sign bracket to the pole using a minimum of 5/8" bolts. For Helicopter Mounted signs, attach BOTTOM bracket first, then TOP. Tighten blades & brackets using set screws.', 'Gently loosen the screws at the top of the base to guide the post through.', 'Slide base up the bottom of the pole (careful not to scratch). Raise it several feet above the 24"-30" line to ensure space for packing concrete later. Temporarily set it with (1) set screw.', 'Dig your hole: 24"-30" deep and about 6"-8" in diameter.', 'Place your assembled sign system into the hole.', 'Once level, fill the hole with DRY concrete powder up to 12" or half-way. Tamp the dry concrete around the post and ensure it is level. (See DRY concrete warning above).', 'Fill in the remaining hole with layers of dirt, tamping and packing as you go. Check frequently that your post is level. Pack dirt tightly.', 'Loosen the temporary set screw from Step 6. Lower the base to the ground. Set the base with all set screws. Level unit once more and pack dirt if needed. Let cure 48-72 hours.'].map((step, idx) => <div key={idx} className="flex gap-4 p-4 bg-white border border-brand-border rounded-lg shadow-sm" data-id="element-1380" data-mp="3mm03j066snsw" data-mpi={idx}>
                  <div className="w-8 h-8 rounded-full bg-brand-charcoal text-white flex items-center justify-center font-bold shrink-0" data-id="element-1381" data-mp="1rl7s31e9jvnd">
                    {idx + 1}
                  </div>
                  <p className="text-brand-muted text-sm pt-1 leading-relaxed" data-id="element-1382" data-mp="21b9i3yq6fo4x">
                    {step}
                  </p>
                </div>)}
            </div>
            <p className="mt-6 text-sm text-brand-muted italic" data-id="element-1383" data-mp="2b7ma8s62ptxk">
              If touch-ups are needed, we recommend Rust-Oleum Textured Black
              Spray Paint after the unit has set.
            </p>
          </div>
        </section>

        {/* Residential Mailbox Instructions */}
        <section className="py-16 bg-brand-goldWash border-b border-brand-border" id="residential" data-id="element-1384" data-mp="2fa0sba1x4pns">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1385" data-mp="pvul4uxyww9t">
            <div className="flex items-center gap-4 mb-8" data-id="element-1386" data-mp="kwhoxjl7icuk">
              <div className="w-14 h-14 rounded-full bg-white text-brand-maroon flex items-center justify-center shadow-sm" data-id="element-1387" data-mp="ky2mli2tibwn">
                <Mail size={28} data-id="element-1388" data-mp="31iobudltr9ta" />
              </div>
              <h2 className="text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1389" data-mp="31hd1fuxvj3t7">
                Residential Mailbox Direct Burial
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-1390" data-mp="2rsd0i5zt9ycd">
              {/* USPS Guidelines & Tips */}
              <div className="bg-white p-6 rounded-xl border border-brand-border shadow-sm" data-id="element-1391" data-mp="2ds28v0qogfq5">
                <h3 className="font-bold text-brand-charcoal mb-4 flex items-center gap-2" data-id="element-1392" data-mp="2it7y2ol4pkux">
                  <MapPin size={18} className="text-brand-maroon" data-id="element-1393" data-mp="24t6w15t4o7dv" />
                  USPS Guidelines & Tips
                </h3>
                <ul className="space-y-3 text-sm text-brand-muted" data-id="element-1394" data-mp="28uip5lb7naqt">
                  <li className="flex items-start gap-2" data-id="element-1395" data-mp="30sguehyxh15">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1396" data-mp="2z7lwnrqrw2r"></span>
                    <span data-id="element-1397" data-mp="3ehdqr8chfwki">
                      Mailbox door must be <strong data-id="element-1398" data-mp="3eg2o5burwuuz">6-8 inches back</strong> from
                      the curb (or road if no curb).
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1399" data-mp="34ixy30xt4f8w">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1400" data-mp="34hd4pen8b2cm"></span>
                    <span data-id="element-1401" data-mp="2zjv5wh2xe3ic">
                      Bottom of the mailbox must sit{' '}
                      <strong data-id="element-1402" data-mp="2vk2tftaevd7p">41-45 inches above</strong> the road surface.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1403" data-mp="2qpq7nclgppss">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1404" data-mp="2ltrny2b2wvyp"></span>
                    <span data-id="element-1405" data-mp="2guolmpmhh3uf">
                      Holes are generally <strong data-id="element-1406" data-mp="2grsmwz2tab4x">16-18 inches deep</strong> and{' '}
                      <strong data-id="element-1407" data-mp="2bu0um3y98n07">8"-10" wide</strong>.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1408" data-mp="27tgv4tq7l7aw">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1409" data-mp="nbfuy4rcco5e"></span>
                    <span data-id="element-1410" data-mp="rbyllvhhzo60">
                      Check with HOA, subdivision rules, and local/state laws
                      prior to installation.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 font-semibold text-brand-maroon" data-id="element-1411" data-mp="11b7dmissyspq">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" data-id="element-1412" data-mp="3n6yvjmofy0m" />
                    <span data-id="element-1413" data-mp="3l2pim60e6sy">
                      If not installed to USPS guidelines, the postal service
                      may deny deliveries.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Required Materials */}
              <div className="bg-white p-6 rounded-xl border border-brand-border shadow-sm" data-id="element-1414" data-mp="id4l3u11qdn6">
                <h3 className="font-bold text-brand-charcoal mb-4 flex items-center gap-2" data-id="element-1415" data-mp="3h1aciemamk5x">
                  <Wrench size={18} className="text-brand-maroon" data-id="element-1416" data-mp="3lzbwd1rjmnig" />
                  Required Materials
                </h3>
                <ul className="space-y-2 text-sm text-brand-muted" data-id="element-1417" data-mp="3dy4cfllcsz9w">
                  <li className="flex items-center gap-2" data-id="element-1418" data-mp="39xu1duipfio6">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1419" data-mp="3nqjrob59n9r3" />{' '}
                    Post-Hole Digger or Shovel
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1420" data-mp="3irpb7furtxa5">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1421" data-mp="2v0kxy2g4mog3" />{' '}
                    Screwdriver
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1422" data-mp="2r1c5d0wtowm4">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1423" data-mp="35023mjvdp2c2" />{' '}
                    Digging Iron
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1424" data-mp="2zyuuohhi1x3t">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1425" data-mp="k8s3z8y5xbyp" />{' '}
                    Tamper Bar or Leveling Tool
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1426" data-mp="f9yrnjum3r04">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1427" data-mp="1x7k6tqoe393f" />{' '}
                    Allen Key/Allen Wrench
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1428" data-mp="224ttwzob38o3">
                    <CheckCircle2 size={16} className="text-brand-green" data-id="element-1429" data-mp="1o86yx2fwyskt" /> 60lb
                    Bag of DRY Concrete Powder
                  </li>
                </ul>
                <p className="mt-4 text-xs text-brand-muted italic" data-id="element-1430" data-mp="2gw2s3x69y2cu">
                  *Mailbox assembly may differ by manufacturer. These are for
                  standard in-ground posts.
                </p>
              </div>
            </div>

            {/* Steps */}
            <h3 className="font-bold text-brand-charcoal text-xl mb-6" data-id="element-1431" data-mp="273wy6hwgpav0">
              Installation Steps
            </h3>
            <div className="space-y-4" data-id="element-1432" data-mp="2zuykr42hi8ok">
              <div className="flex gap-4 p-4 bg-white border border-brand-border rounded-lg shadow-sm" data-id="element-1433" data-mp="2ztwx804a7tpy">
                <div className="w-8 h-8 rounded-full bg-brand-charcoal text-white flex items-center justify-center font-bold shrink-0" data-id="element-1434" data-mp="34m6s7uk97jz3">
                  1
                </div>
                <div className="text-brand-muted text-sm pt-1 leading-relaxed" data-id="element-1435" data-mp="zs5ue45jrptp">
                  <p className="mb-2" data-id="element-1436" data-mp="ust5ldv488oh">
                    Fully assemble mailbox system. Set aside. Double check
                    measurements for hole placement (door 6-8 inches from curb).
                  </p>
                  <p data-id="element-1437" data-mp="1ni9rdso8crbq">
                    Dig hole approx. 16-18 inches deep and 8-10 inches wide.
                    Place mailbox and post into hole and hold steady (a second
                    person is helpful).
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-white border border-brand-border rounded-lg shadow-sm" data-id="element-1438" data-mp="19iii258nrz42">
                <div className="w-8 h-8 rounded-full bg-brand-charcoal text-white flex items-center justify-center font-bold shrink-0" data-id="element-1439" data-mp="256rvcs6bnnw">
                  2
                </div>
                <div className="text-brand-muted text-sm pt-1 leading-relaxed" data-id="element-1440" data-mp="29qfb5pm9mf4k">
                  <p className="mb-2" data-id="element-1441" data-mp="29ocbsez839n6">
                    Level the system. Pour about 1/4 bag of DRY concrete powder
                    into the hole. Tamp around post and ensure level.
                  </p>
                  <p data-id="element-1442" data-mp="2ipaix64yjx9z">
                    Add soil on top of concrete and tamp down. Repeat process
                    with another 1/4 bag of dry concrete. Fill remaining 3
                    inches with dirt until secure.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-white border border-brand-border rounded-lg shadow-sm" data-id="element-1443" data-mp="2xi46leudvjpq">
                <div className="w-8 h-8 rounded-full bg-brand-charcoal text-white flex items-center justify-center font-bold shrink-0" data-id="element-1444" data-mp="31fs66v70u0ny">
                  3
                </div>
                <div className="text-brand-muted text-sm pt-1 leading-relaxed" data-id="element-1445" data-mp="175eriiv10npx">
                  <p className="mb-2" data-id="element-1446" data-mp="1c3gal3sgcn1e">
                    Tamp the top layer of dirt flat.{' '}
                    <strong data-id="element-1447" data-mp="vx9fc4zkyb4s">MAILBOX WILL BE FRAGILE.</strong> Do not shake,
                    move, or use strong force for 48 hours. Fully cured in 72
                    hours.
                  </p>
                  <p className="text-brand-maroon font-medium" data-id="element-1448" data-mp="15tci9d29adss">
                    Moving/shaking while drying creates a concrete-casted hole
                    with gaping, causing a wobbly mailbox.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CBU Installation */}
        <section className="py-16 bg-white border-b border-brand-border" id="cbu" data-id="element-1449" data-mp="1okm3gjle2r07">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1450" data-mp="1tjf61gz50xll">
            <div className="flex items-center gap-4 mb-8" data-id="element-1451" data-mp="1tixdovflj3iy">
              <div className="w-14 h-14 rounded-full bg-brand-charcoal text-brand-gold flex items-center justify-center shadow-sm" data-id="element-1452" data-mp="3qcqr3ivuv254">
                <Building size={28} data-id="element-1453" data-mp="3vblih97w1z0u" />
              </div>
              <h2 className="text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1454" data-mp="2cgh2bjss09b2">
                Cluster Box (CBU) 1570 F Series
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12" data-id="element-1455" data-mp="27ne5n2spcjqn">
              <div className="bg-brand-background p-6 rounded-xl border border-brand-border" data-id="element-1456" data-mp="23o5abti0kdsz">
                <h3 className="font-bold text-brand-charcoal mb-4" data-id="element-1457" data-mp="1yq3tlf828qko">
                  CBU Installation Heights
                </h3>
                <ul className="space-y-2 text-sm text-brand-muted" data-id="element-1458" data-mp="2vcgvuz1ejkwr">
                  <li data-id="element-1459" data-mp="2rdpvmj1n3n5f">
                    <strong data-id="element-1460" data-mp="2rbdhkgz05wxn">Type I:</strong> 8 Tenant Doors
                  </li>
                  <li data-id="element-1461" data-mp="2mcabqub77mom">
                    <strong data-id="element-1462" data-mp="2mfy77z07ej5z">Type II:</strong> 12 Tenant Doors
                  </li>
                  <li data-id="element-1463" data-mp="2hh4rdzxg2g2n">
                    <strong data-id="element-1464" data-mp="1ayyg36pleadj">Type III:</strong> 16 Tenant Doors
                  </li>
                  <li data-id="element-1465" data-mp="1awlo00q4ejmr">
                    <strong data-id="element-1466" data-mp="1604zh5f0e395">Type IV:</strong> 13 Tenant Doors
                  </li>
                </ul>
              </div>

              <div className="lg:col-span-2 bg-brand-background p-6 rounded-xl border border-brand-border" data-id="element-1467" data-mp="17i3odu90r5e6">
                <h3 className="font-bold text-brand-charcoal mb-4" data-id="element-1468" data-mp="1ceu3ae937uy0">
                  Pedestal Installation Steps
                </h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-brand-muted" data-id="element-1469" data-mp="yigkn1sthnhy">
                  <li data-id="element-1470" data-mp="13hjrmrscy3sk">Open Master Loading Doors</li>
                  <li data-id="element-1471" data-mp="oqatfuae3a3t">
                    Remove Left and Right Side 91079 Floating Shelves, exposing
                    mounting holes.
                  </li>
                  <li data-id="element-1472" data-mp="tj42gucw54lp">Place unit on Pedestal</li>
                  <li data-id="element-1473" data-mp="fqw5b1hp3bmh">
                    From underneath unit, insert Four (4) 86101 3/8-16x1.25"
                    Carriage Bolts upward.
                  </li>
                  <li data-id="element-1474" data-mp="5rym8e31gaav">
                    From inside parcel compartments, add four (4) 86097 3/8" x
                    .688 OD Lock Washer and tighten four (4) 86089B 3/8"-16 hex
                    nuts. Torque to 22 foot-pounds.
                  </li>
                  <li data-id="element-1475" data-mp="3namb1jtwte2y">Replace Left and Right Side 91079 Floating Shelves.</li>
                </ol>
              </div>
            </div>

            {/* Diagrams */}
            <h3 className="font-bold text-brand-charcoal text-xl mb-6" data-id="element-1476" data-mp="13flwm37kp5n">
              Installation Diagrams
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1477" data-mp="3s7vwypi3b7m1">
              <div className="border border-brand-border rounded-xl overflow-hidden shadow-sm bg-white p-4" data-id="element-1478" data-mp="a32filpp4qo5">
                <p className="text-center font-bold text-brand-charcoal text-sm mb-3" data-id="element-1479" data-mp="53pu85eh3rno">
                  CBU Heights
                </p>
                <img src="https://drwzpk38qkpfb.cloudfront.net/www.midatlanticmailboxes.com/uploaded/images/authflorence/authflorence/cbuheight.jpg" alt="CBU Installation Heights Diagram" className="w-full h-auto object-contain" data-id="element-1480" data-mp="jvq977ldqein" />
              </div>
              <div className="border border-brand-border rounded-xl overflow-hidden shadow-sm bg-white p-4" data-id="element-1481" data-mp="2ibh14icn8af9">
                <p className="text-center font-bold text-brand-charcoal text-sm mb-3" data-id="element-1482" data-mp="2naagkgbhqbi2">
                  Site & Pad Prep
                </p>
                <img src="https://drwzpk38qkpfb.cloudfront.net/www.midatlanticmailboxes.com/uploaded/images/authflorence/authflorence/paddetail.jpg" alt="CBU Site and Pad Preparation Diagram" className="w-full h-auto object-contain" data-id="element-1483" data-mp="2s2ldrbeujj6x" />
              </div>
              <div className="border border-brand-border rounded-xl overflow-hidden shadow-sm bg-white p-4" data-id="element-1484" data-mp="24dseb4z0mt7k">
                <p className="text-center font-bold text-brand-charcoal text-sm mb-3" data-id="element-1485" data-mp="29d5dmp72psr1">
                  Pedestal Install
                </p>
                <img src="https://drwzpk38qkpfb.cloudfront.net/www.midatlanticmailboxes.com/uploaded/images/authflorence/authflorence/pedestalinstall.jpg" alt="CBU Pedestal Installation Diagram" className="w-full h-auto object-contain" data-id="element-1486" data-mp="3kqpk7z58s0ao" />
              </div>
            </div>
          </div>
        </section>

        {/* Footer Contact Banner */}
        <section className="py-12 bg-brand-charcoal text-center" data-id="element-1487" data-mp="14akxzbi0wsel">
          <div className="max-w-3xl mx-auto px-4" data-id="element-1488" data-mp="zd1arm3cl5ai">
            <p className="text-brand-gold font-bold mb-2" data-id="element-1489" data-mp="zcj3luf5d6nt">
              MidAtlantic Mailbox, Inc
            </p>
            <p className="text-gray-300 text-sm mb-4" data-id="element-1490" data-mp="ublmll5viinw">
              4507 Metropolitan Court, Suite D | Frederick, MD 21704
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-white text-sm" data-id="element-1491" data-mp="lci5gmq9odc1">
              <span data-id="element-1492" data-mp="ge6to6gevsr6">
                Toll Free:{' '}
                <a href="tel:800-806-7997" className="hover:text-brand-gold" data-id="element-1493" data-mp="gfrnftuwj7nz">
                  800-806-7997
                </a>
              </span>
              <span className="hidden sm:inline text-gray-500" data-id="element-1494" data-mp="2ares083limhk">|</span>
              <span data-id="element-1495" data-mp="2aq3peblvzks1">
                Local:{' '}
                <a href="tel:301-620-7585" className="hover:text-brand-gold" data-id="element-1496" data-mp="2tvi99n9t19ef">
                  301-620-7585
                </a>{' '}
                or{' '}
                <a href="tel:301-620-7867" className="hover:text-brand-gold" data-id="element-1497" data-mp="2kvv7h0d5y1ku">
                  301-620-7867
                </a>
              </span>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1498" data-mp="3dktry44rdftb" />
      <MobileStickyBar data-id="element-1499" data-mp="3dkajde1003ry" />
    </div>;
}